/*
 * Copyright (c) 2025 - 2026 Ping Identity Corporation. All rights reserved.
 *
 * This software may be modified and distributed under the terms
 * of the MIT license. See the LICENSE file for details.
 */
import { logger as loggerFn } from '@forgerock/sdk-logger';
import { callbackType } from '@forgerock/sdk-types';
import { isGenericError, isValidWellknownUrl, createWellknownError, } from '@forgerock/sdk-utilities';
import { createJourneyStore } from './client.store.utils.js';
import { configSlice } from './config.slice.js';
import { journeyApi } from './journey.api.js';
import { createStorage } from '@forgerock/storage';
import * as Either from 'effect/Either';
import { createJourneyObject, parseJourneyResponse } from './journey.utils.js';
import { wellknownApi } from './wellknown.api.js';
/**
 * Creates a journey client for AM authentication tree/journey interactions.
 *
 * Journey-client is designed specifically for ForgeRock Access Management (AM) servers.
 * It uses AM-proprietary endpoints for callback-based authentication trees.
 *
 * @param options - Configuration options for the journey client
 * @param options.config - Configuration options (see {@link JourneyClientConfig}); only `serverConfig.wellknown` is required
 * @param options.requestMiddleware - Optional middleware for request customization
 * @param options.logger - Optional logger configuration
 * @returns A journey client instance
 * @throws When the wellknown URL is invalid, the fetch fails, or the response indicates a non-AM server
 *
 * @example
 * ```typescript
 * try {
 *   const client = await journey({
 *     config: {
 *       serverConfig: {
 *         wellknown: 'https://am.example.com/am/oauth2/alpha/.well-known/openid-configuration',
 *       },
 *     },
 *   });
 *   const step = await client.start({ journey: 'Login' });
 * } catch (error) {
 *   console.error('Failed to initialize:', error.message);
 * }
 * ```
 */
export async function journey({ config, requestMiddleware, logger, }) {
    const log = loggerFn({
        level: logger?.level ?? config.log ?? 'error',
        custom: logger?.custom,
    });
    const ignoredProperties = [
        'callbackFactory',
        'clientId',
        'middleware',
        'oauthThreshold',
        'platformHeader',
        'prefix',
        'realmPath',
        'redirectUri',
        'scope',
        'tokenStore',
        'tree',
        'type',
    ];
    const providedIgnored = ignoredProperties.filter((prop) => config[prop] !== undefined);
    if (config.serverConfig?.timeout !== undefined) {
        providedIgnored.push('serverConfig.timeout');
    }
    if (providedIgnored.length > 0) {
        log.warn(`The following configuration properties are not used by journey-client and will be ignored: ${providedIgnored.join(', ')}`);
    }
    const store = createJourneyStore({ requestMiddleware, logger: log });
    const { wellknown } = config.serverConfig;
    if (!isValidWellknownUrl(wellknown)) {
        const message = `Invalid wellknown URL: ${wellknown}. URL must use HTTPS (or HTTP for localhost) and end with /.well-known/openid-configuration.`;
        log.error(message);
        throw new Error(message);
    }
    const { data: wellknownResponse, error: fetchError } = await store.dispatch(wellknownApi.endpoints.configuration.initiate(wellknown));
    if (fetchError || !wellknownResponse) {
        const error = createWellknownError(fetchError);
        const message = `${error.error}: ${error.message}`;
        log.error(message);
        throw new Error(message);
    }
    store.dispatch(configSlice.actions.set({
        wellknownResponse: wellknownResponse,
    }));
    const configError = store.getState().config.error;
    if (configError) {
        const message = `${configError.error}: ${configError.message}`;
        log.error(message);
        throw new Error(message);
    }
    const stepStorage = createStorage({
        type: 'sessionStorage',
        name: 'journey-step',
    });
    const self = {
        subscribe: store.subscribe,
        start: async (options) => {
            const response = await store.dispatch(journeyApi.endpoints.start.initiate(options));
            return Either.match(parseJourneyResponse(response), {
                onLeft: (err) => err,
                onRight: (step) => createJourneyObject(step),
            });
        },
        /**
         * Submits the current Step payload to the authentication API and retrieves the next JourneyStep in the journey.
         */
        next: async (step, options) => {
            const response = await store.dispatch(journeyApi.endpoints.next.initiate({ step, options }));
            return Either.match(parseJourneyResponse(response), {
                onLeft: (err) => err,
                onRight: (step) => createJourneyObject(step),
            });
        },
        // TODO: Remove the actual redirect from this method and just return the URL to the caller
        redirect: async (step) => {
            const cb = step.getCallbackOfType(callbackType.RedirectCallback);
            if (!cb) {
                // TODO: Remove throwing errors from SDK and use Result types instead
                throw new Error('RedirectCallback not found on step');
            }
            const redirectUrl = cb.getRedirectUrl();
            if (!redirectUrl || typeof redirectUrl !== 'string') {
                throw new Error('Redirect URL not found on RedirectCallback');
            }
            const err = await stepStorage.set({ step: step.payload });
            if (isGenericError(err)) {
                log.warn('Failed to persist step before redirect', err);
            }
            window.location.assign(redirectUrl);
        },
        resume: async (url, options) => {
            const parsedUrl = new URL(url);
            const code = parsedUrl.searchParams.get('code');
            const error = parsedUrl.searchParams.get('error');
            const errorCode = parsedUrl.searchParams.get('errorCode');
            const errorMessage = parsedUrl.searchParams.get('errorMessage');
            const state = parsedUrl.searchParams.get('state');
            const form_post_entry = parsedUrl.searchParams.get('form_post_entry');
            const nonce = parsedUrl.searchParams.get('nonce');
            const RelayState = parsedUrl.searchParams.get('RelayState');
            const responsekey = parsedUrl.searchParams.get('responsekey');
            const scope = parsedUrl.searchParams.get('scope');
            const suspendedId = parsedUrl.searchParams.get('suspendedId');
            const authIndexValue = parsedUrl.searchParams.get('authIndexValue') ?? undefined;
            let previousStep;
            function requiresPreviousStep() {
                return (code && state) || form_post_entry || responsekey;
            }
            function isStoredStep(obj) {
                return (typeof obj === 'object' &&
                    obj !== null &&
                    'step' in obj &&
                    typeof obj.step === 'object');
            }
            if (requiresPreviousStep()) {
                const stored = await stepStorage.get();
                if (stored) {
                    if (isGenericError(stored)) {
                        throw new Error(`Error retrieving previous step: ${stored.message || stored.error}`);
                    }
                    else if (isStoredStep(stored)) {
                        previousStep = createJourneyObject(stored.step);
                    }
                }
                await stepStorage.remove();
                if (!previousStep) {
                    throw new Error('Error: previous step information not found in storage for resume operation.');
                }
            }
            const resumeOptions = {
                ...options,
                query: {
                    ...(code && { code }),
                    ...(error && { error }),
                    ...(errorCode && { errorCode }),
                    ...(errorMessage && { errorMessage }),
                    ...(form_post_entry && { form_post_entry }),
                    ...(nonce && { nonce }),
                    ...(RelayState && { RelayState }),
                    ...(responsekey && { responsekey }),
                    ...(scope && { scope }),
                    ...(state && { state }),
                    ...(suspendedId && { suspendedId }),
                    ...(options && options.query),
                },
                ...((options?.journey ?? authIndexValue) && {
                    journey: options?.journey ?? authIndexValue,
                }),
            };
            if (previousStep) {
                return await self.next(previousStep, resumeOptions);
            }
            else {
                // TODO: We should better handle this type misalignment
                const startOptions = resumeOptions;
                return await self.start(startOptions);
            }
        },
        /**
         * Ends the current authentication session by calling the `/sessions` endpoint.
         */
        terminate: async (options) => {
            const { error } = await store.dispatch(journeyApi.endpoints.terminate.initiate(options));
            if (error) {
                return {
                    error: 'terminate_failed',
                    message: 'status' in error
                        ? `Failed to terminate session: ${error.status}`
                        : 'Failed to terminate session',
                    type: 'unknown_error',
                };
            }
        },
    };
    return self;
}
